import clsx from "clsx";
import { Suspense, useRef } from "react";
import { Toaster } from "react-hot-toast";
import { useMetaTags } from "react-metatags-hook";
import { NavLink, Outlet, useLocation } from "react-router";
import logoGeneric from "@/assets/logos/generic.svg";
import SJLogo from "@/assets/logos/sj_logo.svg";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import DevModal from "@/components/shared/dev-modal";
import { scheduleInfo } from "@/data/information";
import useScrollRestoration from "@/hooks/useScrollRestoration";
import { defaultOpenGraphImage, isTestMode } from "@/util/const";

export default function SchedulePageLayout() {
  const location = useLocation();

  const { title, name, description, shortDescription } = scheduleInfo;

  const metaImage = defaultOpenGraphImage;

  useMetaTags(
    {
      title,
      description: description || shortDescription,
      openGraph: {
        image: metaImage,
        description: description || shortDescription,
      },
    },
    [name, description, shortDescription, metaImage],
  );
  const scrollRef = useRef<HTMLElement | null>(null);

  const childRef = useRef<HTMLElement | null>(null);
  useScrollRestoration({
    scrollRef: scrollRef as import("react").RefObject<HTMLElement>,
    childRef: childRef as import("react").RefObject<HTMLElement>,
    pathname: location.pathname,
  });

  function setScrollRef(div: HTMLElement | null) {
    scrollRef.current = div;
  }

  function setChildRef(div: HTMLElement | null) {
    childRef.current = div;
  }

  return (
    <>
      <Toaster>
        {(t) => (
          <div
            role="alert"
            className={`${
              t.visible ? "animate-enter" : "animate-leave"
            } alert max-w-5xl ${t.className}`}
          >
            {t.message as React.ReactNode}
          </div>
        )}
      </Toaster>
      <div className="flex flex-col w-full h-full overflow-hidden relative">
        <Header />
        <DevModal />
        <div
          className="scroll-ref w-full grow flex flex-col overflow-y-auto overflow-x-hidden"
          ref={setScrollRef}
        >
          <div className="h-full flex flex-col">
            <div className="grow" ref={setChildRef}>
              <Suspense fallback={null}>
                <Outlet />
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Header() {
  const logoInner = (
    <>
      <img alt="St. Jude Logo" src={SJLogo} className={!isTestMode ? "w-10" : "hidden"} />
      <img
        alt="Play Live Tools Logo"
        src={`${logoGeneric}`}
        className={isTestMode ? "w-8 fill-gray-600 opacity-50" : "hidden"}
      />
      <span className={!isTestMode ? "" : "hidden"}>St. Jude</span>
      <span className={"text-primary-content"}>Play Live Schedule</span>
    </>
  );

  return (
    <div className={clsx(["navbar bg-primary text-primary-content items-center flex-col"])}>
      <div className="grow md:justify-between transition-all flex items-center w-full">
        <div className="flex-1 hidden lg:block">
          <NavLink className="btn-ghost no-animation text-xl btn" to="/">
            {logoInner}
          </NavLink>
        </div>

        <div className="flex-2 md:hidden">
          <NavLink to="/" className="text-lg pr-5 no-animation w-full btn btn-ghost">
            {logoInner}
          </NavLink>
        </div>
        <div className="flex-none"></div>
        <ThemeToggle />
      </div>
    </div>
  );
}
